import {Dialog} from "@mui/material";
import {useTranslation} from "../../utils/localization.js";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import {Helmet} from "react-helmet";
import WaitingAuthBackdrop from "../WaitingAuthBackdrop/index.jsx";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import {Link as RouterLink} from "react-router-dom";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {AuthorizationButton} from "../AuthorizationButton/index.jsx";
import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {
  accountSelector, hasValidRegisteredCredentials,
  useAuth,
  useCreateUserWithCredentials, useRefreshProfile,
  userProfileSelector
} from "../../utils/platform_auth.jsx";
import emailValidator from "email-validator";
import {MultilineTypography} from "../MultilineTypography/index.jsx";
import {useRecoilValueLoadable} from "recoil";
import PTRestAPI from "../../utils/pt_rest_api.jsx";

export default function ConnectAccount() {
  const auth = useAuth()
  const {state: profileState, contents: profile} = useRecoilValueLoadable(userProfileSelector)
  const {state: accountState, contents: account} = useRecoilValueLoadable(accountSelector)
  const nameRef = useRef()
  const emailRef = useRef()
  const theme = useTheme()
  const refreshProfile = useRefreshProfile()

  const [connectError, setConnectError] = useState(false)
  const { state, error, createUser } = useCreateUserWithCredentials()
  const t = useTranslation()

  useEffect(() => {
    if (error) {
      setConnectError(error)
    }
  }, [error])

  function clearEmailError() {
    if (connectError) setConnectError(false)
  }

  async function connectWithEmail() {
    if (auth.email) {
      setConnectError(t`Already Have Email`)
      return
    }

    const email = emailRef.current.value
    const name = nameRef.current.value
    if (emailValidator.validate(email))
      await createUser({ email })
    else
      setConnectError(t`Invalid email`)

    if (name) {
      await PTRestAPI.ChangeUserName(auth, name)
      refreshProfile()
    }
  }

  return <Dialog open={accountState === 'hasValue' && account && account.account_type !== 'free' && !hasValidRegisteredCredentials(auth)} maxWidth='1200px'>
    <DialogContent sx={{p: '40px'}}>
      <Grid sx={{width: '100%'}} container>
        <Helmet>
          <title>Aldoo - Connect Your Account</title>
        </Helmet>
        <WaitingAuthBackdrop
          email={emailRef?.current?.value}
          open={state && state !== 'success'}
        />
        <Grid item xs={12} sm={5} sx={{ justifyContent: 'center' }}>
          <Stack direction="column">
            <Typography variant="h3">{t`Connect your`}</Typography>
            <Typography variant="h3">Aldoo</Typography>
            <Typography variant="h3">{t`account`}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={7}>
          <Stack
            component="form"
            noValidate
            autoComplete="off"
            sx={{ alignContent: 'flex-start' }}
            spacing={1}
          >
            <MultilineTypography
              text={t`Register an account|to protect your purchase!`}
              props={{[1]: { color: 'primary', lineHeight: 0.3 }}}
              variant="h3"
              sx={{ [theme.breakpoints.down('sm')]: { visibility: 'hidden' } }}
            />
            {profileState === 'hasValue' && profile?.name === 'Aldoo User'  && <TextField
              id="standard-basic"
              label={t`Name`}
              variant="standard"
              inputRef={nameRef}
              onChange={clearEmailError}
            />}
            <TextField
              id="standard-basic"
              label={t`Email`}
              variant="standard"
              inputRef={emailRef}
              onChange={clearEmailError}
              helperText={connectError}
              error={!!connectError}
            />
            <Box sx={{ height: '0.5rem' }} />
            <Button onClick={connectWithEmail} variant="contained">
              {t`Connect`}
            </Button>
            <Box sx={{ height: '0.5rem' }} />
            <AuthorizationButton
              provider="google"
              label={t`Connect with Google`}
              onCompleted={createUser}
            />
            <AuthorizationButton
              provider="apple"
              label={t`Connect with Apple`}
              onCompleted={createUser}
            />
            {
              //TODO: Removed since FB disabled the app due to Android Crash
              /* <AuthorizationButton
              provider="facebook"
              label="Continue with Facebook"
              onCompleted={startLogin}
            /> */
            }
            <Typography sx={{ display: 'block', a: { display: 'inline' } }}>
              {t`By signing in you agree with our`}
              <Link
                color="primary"
                variant="body1"
                component={RouterLink}
                to="/terms-of-use"
              >
                {' '}
                {t`Term of Use`}
              </Link>{' '}
              {t`and`}
              <Link
                color="primary"
                variant="body1"
                component={RouterLink}
                to="/privacy-policy"
              >
                {' '}
                {t`Privacy Policy`}
              </Link>
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </DialogContent>
  </Dialog>
}